import {
  selectFinalSendBlock,
  type FinalSendBlock,
} from './shared-responder-v2-core-completion.helpers';
import { resolveForwardIntent } from './shared-responder-v2-core-forward.helpers';
import type { QuestionTurnResult } from './shared-responder-v2-core-gateway.types';
import {
  isRequiredAnswerMissing,
  selectIsDistributionAvailable,
} from './shared-responder-v2-core-session-answer.selectors';
import {
  isResultOnCurrentQuestion,
  resolveNextIndex,
} from './shared-responder-v2-core-session-machine.helpers';
import { hasUnansweredFollowUp } from './shared-responder-v2-core-session-turns.helpers';
import { RESPONDER_PHASES } from './shared-responder-v2-core-session.consts';
import {
  selectCurrentThread,
  selectIsBlockedBySiteGate,
} from './shared-responder-v2-core-session.selectors';
import type { ResponderSessionState } from './shared-responder-v2-core-session.types';
import { isOtherTextMissing } from './shared-responder-v2-core-thread-choice';
import { THREAD_STATUSES } from './shared-responder-v2-core-thread.consts';

/**
 * Live results are entitled once the answer lands; the accepted turn must not walk past
 * them. A later bare Continue (`continuePressed`) leaves — same hold pattern as a follow-up.
 * Hiding the rows still holds: availability, not visibility, owns the gate.
 */
function awaitsLiveDistributionView(
  state: ResponderSessionState,
  questionId: string
): boolean {
  return selectIsDistributionAvailable(state, questionId);
}

/**
 * The backend can close a question on the very turn that answers the responder (`replyFeedback`
 * alongside `isQuestionComplete: true`). Walking on would unmount that reply unread, so the turn
 * holds; the settled question's page Continue leaves — same hold pattern as live distribution.
 */
function awaitsReplyFeedbackRead(result: QuestionTurnResult): boolean {
  return result.replyFeedback !== null;
}

/**
 * The conditions under which the reducer may move the cursor at all, shared by both directions
 * of the decision so they can't disagree. Excludes leaving the questions: the last step's
 * forward move belongs to `shouldFinishOnAcceptedTurn`, since a Continue can't know a follow-up
 * is still owed.
 */
function canAdvanceCursor(state: ResponderSessionState): boolean {
  if (state.phase !== RESPONDER_PHASES.Question) {
    return false;
  }

  if (state.isComplete || state.editing.mode === 'editing') {
    return false;
  }

  return resolveNextIndex(state) <= state.steps.length - 1;
}

/**
 * Whether a forward turn should leave the advance to the reply it is about to provoke — true
 * whenever the cursor is free to move. Whether a follow-up is coming is knowable only from the
 * accepted turn's `isQuestionComplete`/`nextQuestion`, not from local `aiConvoDepth` arithmetic,
 * so nothing local is consulted here — `shouldAdvanceOnAcceptedTurn` is the sole advance
 * authority; depth may still colour the UI, never the cursor. Leaving the questions is excluded
 * by `canAdvanceCursor`; `shouldFinishOnAcceptedTurn` makes that `Question -> Outro` move instead.
 */
export function shouldDeferAdvanceToAcceptedTurn(
  state: ResponderSessionState
): boolean {
  return canAdvanceCursor(state);
}

/**
 * Whether a Continue that carries no new answer may move the cursor on its own. The thread's
 * own status is the wire's verdict (`appendAssistantTurns` closes it on `isQuestionComplete`),
 * so anything short of `Closed` on an answered thread — open follow-up, in-flight or rejected
 * turn — keeps the responder in place; advancing anyway would desync the backend cursor and get
 * `addQuestionMessage` refused on the next question. Editing is exempt, since its Continue
 * returns to the step it came from.
 */
export function isForwardAdvanceAllowed(state: ResponderSessionState): boolean {
  if (state.editing.mode === 'editing') {
    return true;
  }

  const thread = selectCurrentThread(state);

  if (selectIsBlockedBySiteGate(state)) {
    return false;
  }

  if (isRequiredAnswerMissing(state)) {
    return false;
  }

  if (thread === null || thread.answer === null) {
    return !isOtherTextMissing(thread?.draft ?? null);
  }

  return (
    thread.status === THREAD_STATUSES.Closed && !hasUnansweredFollowUp(thread)
  );
}

export function resolveForwardSendBlock(
  state: ResponderSessionState
): FinalSendBlock | null {
  return resolveForwardIntent(state).type === 'advance'
    ? null
    : selectFinalSendBlock(state);
}

export function isAdvanceRefused(state: ResponderSessionState): boolean {
  if (resolveForwardSendBlock(state) !== null) {
    return true;
  }

  return (
    resolveForwardIntent(state).type === 'advance' &&
    !isForwardAdvanceAllowed(state)
  );
}

/**
 * Why a Continue that carries nothing new was refused — the copy the responder
 * is shown so the button is never a silent no-op.
 */
export const FORWARD_BLOCK_REASONS = {
  /** An AI follow-up is on screen with no reply typed or dictated yet. */
  FollowUpUnanswered: 'followUpUnanswered',
  /** The last turn was rejected; the retry control is the way forward. */
  TurnFailed: 'turnFailed',
  /** The answer is still on its way to the backend. */
  TurnInFlight: 'turnInFlight',
  /** The question's site has never been opened. */
  SiteUnopened: 'siteUnopened',
  AnswerRequired: 'answerRequired',
  OtherTextMissing: 'otherTextMissing',
} as const;

export type ForwardBlockReason =
  (typeof FORWARD_BLOCK_REASONS)[keyof typeof FORWARD_BLOCK_REASONS];

/**
 * The explanation for `isForwardAdvanceAllowed` answering no, or `null` when it answered yes.
 * Every refusal named here must be actionable by the responder — an unnamed one looks like a
 * broken button, a dead end in voice mode, which has no composer to send the reply from.
 */
export function resolveForwardBlockReason(
  state: ResponderSessionState
): ForwardBlockReason | null {
  if (resolveForwardSendBlock(state) !== null) {
    return FORWARD_BLOCK_REASONS.AnswerRequired;
  }

  if (isForwardAdvanceAllowed(state)) {
    return null;
  }

  const thread = selectCurrentThread(state);

  if (thread === null) {
    return null;
  }

  if (selectIsBlockedBySiteGate(state)) {
    return FORWARD_BLOCK_REASONS.SiteUnopened;
  }

  if (hasUnansweredFollowUp(thread)) {
    return FORWARD_BLOCK_REASONS.FollowUpUnanswered;
  }

  if (isOtherTextMissing(thread.draft)) {
    return FORWARD_BLOCK_REASONS.OtherTextMissing;
  }

  if (isRequiredAnswerMissing(state)) {
    return FORWARD_BLOCK_REASONS.AnswerRequired;
  }

  return thread.status === THREAD_STATUSES.Failed
    ? FORWARD_BLOCK_REASONS.TurnFailed
    : FORWARD_BLOCK_REASONS.TurnInFlight;
}

/**
 * Whether an accepted answer is itself the forward trigger — the composer-answered counterpart
 * of the optimistic advance a surface pick gets, needed because a question with no follow-up
 * closes its thread with nothing on screen (Send disabled, no page Continue) to otherwise
 * produce a second forward intent.
 *
 * Guards, each a state the responder/shell must still own:
 * - a turn the backend hasn't called complete is still open;
 * - a follow-up is waiting (`aiConvoDepth: 1` reaches here with its follow-up already appended),
 *   the turn carries a `replyFeedback` the responder has yet to read, or live distribution is
 *   available to view;
 * - a late reply for a left question must not move the cursor;
 * - an edit returns through `editCancelled`/`continuePressed`, not here;
 * - a skip or bare history hydration carries no answer;
 * - a terminal action already routed the phase off `Question`;
 * - leaving the questions is the shell's transition (`Question -> Outro` runs the completion
 *   check), so the last step is deliberately left alone.
 */
export function shouldAdvanceOnAcceptedTurn(
  state: ResponderSessionState,
  questionId: string,
  result: QuestionTurnResult
): boolean {
  if (!result.isComplete || !canAdvanceCursor(state)) {
    return false;
  }

  if (!isResultOnCurrentQuestion(state, questionId)) {
    return false;
  }

  const thread = state.threads[questionId];

  if (thread === undefined || thread.answer === null) {
    return false;
  }

  return !(
    hasUnansweredFollowUp(thread) ||
    awaitsReplyFeedbackRead(result) ||
    awaitsLiveDistributionView(state, questionId)
  );
}

/**
 * The last step's counterpart to `shouldAdvanceOnAcceptedTurn`: whether an accepted turn itself
 * finished the survey and should cross into the outro. Only the completing turn's
 * `isComplete`/`isQuestionComplete` confirms this — the local answer alone isn't enough, since a
 * turn that leaves the question open must hold the responder there for the follow-up rather than
 * stranding them on an unearned outro. A `replyFeedback` or live distribution available after the
 * answer holds the same way — a later bare Continue finishes. Fires only when the next index leaves the questions,
 * mirroring `canAdvanceCursor`'s last guard.
 */
export function shouldFinishOnAcceptedTurn(
  state: ResponderSessionState,
  questionId: string,
  result: QuestionTurnResult
): boolean {
  if (
    !result.isComplete ||
    state.phase !== RESPONDER_PHASES.Question ||
    state.editing.mode === 'editing'
  ) {
    return false;
  }

  if (!isResultOnCurrentQuestion(state, questionId)) {
    return false;
  }

  if (
    !result.isSurveyComplete &&
    resolveNextIndex(state) <= state.steps.length - 1
  ) {
    return false;
  }

  const thread = state.threads[questionId];

  if (thread === undefined || thread.answer === null) {
    return false;
  }

  return !(
    hasUnansweredFollowUp(thread) ||
    awaitsReplyFeedbackRead(result) ||
    awaitsLiveDistributionView(state, questionId)
  );
}
